import React, { useState, useEffect, useCallback } from 'react';
import { getAnalysisTypes, getAnalysisTypeDetails, submitAnalysisJob } from '../../api/api';

const availableTimeframes = ["M1", "M5", "M15", "H1", "H4", "D1", "W1", "MN1"];

const AnalysisJobForm = ({ instruments, onJobSubmitted, onError }) => {
  // Form state
  const [jobName, setJobName] = useState('');
  const [analysisTypes, setAnalysisTypes] = useState([]);
  const [selectedInstrument, setSelectedInstrument] = useState('');
  const [selectedAnalysisType, setSelectedAnalysisType] = useState('');
  const [targetTimeframe, setTargetTimeframe] = useState('M15');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Dynamic params state
  const [indicatorConfigs, setIndicatorConfigs] = useState([]);
  const [analysisParams, setAnalysisParams] = useState({});

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [formError, setFormError] = useState(null);

  // Fetch available analysis types on mount
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const types = await getAnalysisTypes();
        setAnalysisTypes(types);
        if (types.length > 0) {
          setSelectedAnalysisType(types[0].value);
        }
      } catch (error) {
        setFormError("Could not load analysis types.");
        if (onError) onError("Could not load analysis types.");
      }
    };
    fetchTypes();
  }, [onError]);

  // Fetch details when analysis type changes
  useEffect(() => {
    if (!selectedAnalysisType) {
      setIndicatorConfigs([]);
      setAnalysisParams({});
      return;
    }

    const fetchDetails = async () => {
      setIsLoadingDetails(true);
      setFormError(null);
      try {
        const details = await getAnalysisTypeDetails(selectedAnalysisType);
        if (Array.isArray(details)) {
          // Handle direct array response for indicators
          setIndicatorConfigs(details);
          setAnalysisParams({});
        } else {
          // Handle object response with keys
          setIndicatorConfigs(details.indicator_configs || []);
          setAnalysisParams(details.analysis_params || {});
        }
      } catch (error) {
        setIndicatorConfigs([]);
        setAnalysisParams({});
        const errorMessage = `Could not load details for ${selectedAnalysisType}: ${error.message}`;
        setFormError(errorMessage);
        if (onError) onError(errorMessage);
      } finally {
        setIsLoadingDetails(false);
      }
    };

    fetchDetails();
  }, [selectedAnalysisType, onError]);

  const handleIndicatorParamChange = (indicatorIndex, paramName, value) => {
    const updatedConfigs = [...indicatorConfigs];
    updatedConfigs[indicatorIndex].params[paramName] = value;
    setIndicatorConfigs(updatedConfigs);
  };

  const handleAnalysisParamChange = (paramName, value) => {
    setAnalysisParams(prev => ({ ...prev, [paramName]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (!selectedInstrument || !selectedAnalysisType || !targetTimeframe || !startDate || !endDate) {
      const errorMsg = "All required fields must be filled.";
      setFormError(errorMsg);
      if (onError) onError(errorMsg);
      return;
    }
    if (new Date(startDate) >= new Date(endDate)) {
      const errorMsg = "End date must be after start date.";
      setFormError(errorMsg);
      if (onError) onError(errorMsg);
      return;
    }

    const jobData = {
      name: jobName,
      instrument_symbol: selectedInstrument,
      analysis_type: selectedAnalysisType,
      target_timeframe: targetTimeframe,
      start_date: startDate,
      end_date: endDate,
      indicator_configs: indicatorConfigs,
      analysis_params: analysisParams,
    };

    setIsSubmitting(true);
    try {
      const result = await submitAnalysisJob(jobData);
      if (onJobSubmitted) onJobSubmitted(result);
      // Reset only some fields for convenience
      setJobName('');
      setSelectedInstrument('');
      setStartDate('');
      setEndDate('');
    } catch (error) {
      const errorMessage = `Submission failed: ${error.message}`;
      setFormError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {formError && <p className="text-red-500 bg-red-100 p-3 rounded-md">{formError}</p>}
      
      {/* Core Fields */}
      <div className="space-y-4 p-4 border border-borderDefault rounded-lg">
        <h3 className="font-semibold text-lg text-textPrimary">Core Settings</h3>
        <div>
          <label htmlFor="jobName" className="block text-sm font-medium text-textSecondary">Job Name</label>
          <input type="text" id="jobName" value={jobName} onChange={(e) => setJobName(e.target.value)} className="mt-1 block w-full p-2 border border-borderDefault rounded-md shadow-sm bg-backgroundInput text-textPrimary" placeholder="e.g., ATR Squeeze Analysis" />
        </div>
        <div>
          <label htmlFor="instrument" className="block text-sm font-medium text-textSecondary">Instrument</label>
          <select id="instrument" value={selectedInstrument} onChange={(e) => setSelectedInstrument(e.target.value)} className="mt-1 block w-full p-2 border border-borderDefault rounded-md shadow-sm bg-backgroundInput text-textPrimary" disabled={instruments.length === 0}>
            <option value="" disabled>Select an instrument</option>
            {instruments.map((inst) => (
              <option key={inst.symbol} value={inst.symbol} disabled={inst.data_status !== 'AVAILABLE'}>
                {inst.symbol} ({inst.exchange}) - {inst.data_status === 'AVAILABLE' ? 'Available' : 'Data Not Ready'}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="analysisType" className="block text-sm font-medium text-textSecondary">Analysis Type</label>
          <select id="analysisType" value={selectedAnalysisType} onChange={(e) => setSelectedAnalysisType(e.target.value)} className="mt-1 block w-full p-2 border border-borderDefault rounded-md shadow-sm bg-backgroundInput text-textPrimary" disabled={analysisTypes.length === 0}>
            {analysisTypes.length === 0 ? <option>Loading types...</option> : analysisTypes.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="targetTimeframe" className="block text-sm font-medium text-textSecondary">Target Timeframe</label>
            <select id="targetTimeframe" value={targetTimeframe} onChange={(e) => setTargetTimeframe(e.target.value)} className="mt-1 block w-full p-2 border border-borderDefault rounded-md shadow-sm bg-backgroundInput text-textPrimary">
              {availableTimeframes.map((tf) => <option key={tf} value={tf}>{tf}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-textSecondary">Start Date</label>
            <input type="date" id="startDate" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1 block w-full p-2 border border-borderDefault rounded-md shadow-sm bg-backgroundInput text-textPrimary" />
          </div>
          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-textSecondary">End Date</label>
            <input type="date" id="endDate" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1 block w-full p-2 border border-borderDefault rounded-md shadow-sm bg-backgroundInput text-textPrimary" />
          </div>
        </div>
      </div>

      {/* Dynamic Indicator and Analysis Params */}
      {isLoadingDetails && <p className="text-textSecondary">Loading analysis details...</p>}
      
      {!isLoadingDetails && indicatorConfigs.length > 0 && (
        <div className="space-y-4 p-4 border border-borderDefault rounded-lg">
          <h3 className="font-semibold text-lg text-textPrimary">Indicator Settings</h3>
          {indicatorConfigs.map((config, index) => (
            <div key={index} className="p-3 border border-borderDefault/50 rounded-md bg-backgroundBody">
              <p className="font-medium text-textPrimary">{config.name} ({config.output_name})</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 mt-2">
                {Object.entries(config.params).map(([paramName, paramValue]) => (
                  <div key={paramName}>
                    <label htmlFor={`indicator-${index}-${paramName}`} className="block text-xs font-medium text-textSecondary capitalize">{paramName}</label>
                    <input
                      type="text"
                      id={`indicator-${index}-${paramName}`}
                      value={paramValue}
                      onChange={(e) => handleIndicatorParamChange(index, paramName, e.target.value)}
                      className="mt-1 block w-full p-1.5 border border-borderDefault rounded-md shadow-sm text-sm bg-backgroundInput text-textPrimary"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoadingDetails && Object.keys(analysisParams).length > 0 && (
        <div className="space-y-4 p-4 border border-borderDefault rounded-lg">
          <h3 className="font-semibold text-lg text-textPrimary">Analysis Parameters</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
            {Object.entries(analysisParams).map(([paramName, paramValue]) => (
              <div key={paramName}>
                <label htmlFor={`analysis-param-${paramName}`} className="block text-xs font-medium text-textSecondary capitalize">{paramName.replace(/_/g, ' ')}</label>
                <input
                  type="text"
                  id={`analysis-param-${paramName}`}
                  value={paramValue}
                  onChange={(e) => handleAnalysisParamChange(paramName, e.target.value)}
                  className="mt-1 block w-full p-1.5 border border-borderDefault rounded-md shadow-sm text-sm bg-backgroundInput text-textPrimary"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <button type="submit" disabled={isSubmitting || isLoadingDetails || instruments.length === 0} className="w-full py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-dark disabled:opacity-50">
        {isSubmitting ? 'Submitting...' : 'Submit Analysis Job'}
      </button>
    </form>
  );
};

export default AnalysisJobForm;
